import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ICategoryTarget, INegativeKeywordTarget } from '@ts/common-types';

interface Product {
    id: string;
    sku: string;
    sku_title: string;
    priority: number; // 0 = no priority, 1,2,3... = priority order
    disabled?: boolean; // true = disabled in cluster, false/undefined = enabled
    [key: string]: any;
}

interface KeywordCohort {
    id: string;
    keywordTypes: string[];
    keywords: any[];
    negativeKeywords?: INegativeKeywordTarget[];
    categories?: ICategoryTarget[];
    createdAt?: string;
}

interface Collection {
    id?: string;
    campaignType: string;
    keywordType: string | string[];
    keywords: string[] | any[];
    negative_keywords?: INegativeKeywordTarget[];
    categories?: ICategoryTarget[];
    keywordCohorts?: KeywordCohort[];
    city: string | string[];
    timeSlot: string;
    budget: string;
    type: string;
    startDate?: any;
    campaigns?: Array<any>;
}

interface Cluster {
    id: string;
    name: string;
    createdAt: string;
    products: Product[];
    totalBudget?: string;
    startDate?: any;
    endDate?: any;
    budgetUsed?: number;
    collections: Collection[];
}

interface ClustersState {
    clusters: Cluster[];
    currentClusterId: string | null;
}

const initialState: ClustersState = {
    clusters: [],
    currentClusterId: null,
};

const clustersSlice = createSlice({
    name: 'clusters',
    initialState,
    reducers: {
        // Remove a cluster
        removeCluster: (state, action: PayloadAction<string>) => {
            const clusterId = action.payload;
            state.clusters = state.clusters.filter(c => c.id !== clusterId);
        },
        // Set clusters data
        setClusters: (state, action: PayloadAction<Cluster[]>) => {
            state.clusters = action.payload;
        },
        
        // Add a new cluster
        addCluster: (state, action: PayloadAction<{
            clusterId: string;
            name: string;
            products: Product[];
        }>) => {
            const { clusterId, name, products } = action.payload;
            const newCluster: Cluster = {
                id: clusterId,
                name: name,
                createdAt: new Date().toISOString(),
                products: products,
                collections: []
            };
            state.clusters.push(newCluster);
        },
        
        // Set current cluster
        setCurrentCluster: (state, action: PayloadAction<string>) => {
            state.currentClusterId = action.payload;
        },
        
        // Update product priority in a cluster
        updateProductPriority: (state, action: PayloadAction<{
            clusterId: string;
            productId: string;
            priority: number;
        }>) => {
            const { clusterId, productId, priority } = action.payload;
            const clusters = state.clusters || [];
            const cluster = clusters.find(c => c.id === clusterId);
            
            if (cluster) {
                const product = cluster.products.find(p => p.id === productId);
                if (product) {
                    product.priority = priority;
                }
            }
        },
        
        // Toggle product priority (set/unset)
        toggleProductPriority: (state, action: PayloadAction<{
            clusterId: string;
            productId: string;
        }>) => {
            const { clusterId, productId } = action.payload;
            const clusters = state.clusters || [];
            const cluster = clusters.find(c => c.id === clusterId);
            
            if (cluster) {
                const product = cluster.products.find(p => p.id === productId);
                if (product) {
                    if (product.priority === 0) {
                        // Set priority - find next available number
                        const existingPriorities = cluster.products
                            .filter(p => p.priority > 0)
                            .map(p => p.priority)
                            .sort((a, b) => a - b);
                        
                        let nextPriority = 1;
                        for (const priority of existingPriorities) {
                            if (nextPriority === priority) {
                                nextPriority++;
                            } else {
                                break;
                            }
                        }
                        product.priority = nextPriority;
                    } else {
                        // Remove priority and shift others down
                        const removedPriority = product.priority;
                        product.priority = 0;
                        
                        cluster.products.forEach(p => {
                            if (p.priority > removedPriority) {
                                p.priority = p.priority - 1;
                            }
                        });
                    }
                }
            }
        },

        // Restore all priorities in a cluster to their original DB values
        restoreClusterPrioritiesToOriginal: (state, action: PayloadAction<string>) => {
            const clusterId = action.payload;
            const clusters = state.clusters || [];
            const cluster = clusters.find(c => c.id === clusterId);

            if (cluster) {
                cluster.products.forEach(product => {
                    product.priority = product.priority_value || 0;
                });
            }
        },

        // Restore variant selections to original DB values (same pattern as restoreClusterPrioritiesToOriginal)
        restoreClusterVariantsToOriginal: (state, action: PayloadAction<string>) => {
            const clusterId = action.payload;
            const clusters = state.clusters || [];
            const cluster = clusters.find(c => c.id === clusterId);

            if (cluster) {
                cluster.products.forEach(product => {
                    if (product.skuDetails && Array.isArray(product.skuDetails)) {
                        product.skuDetails.forEach((skuDetail: any) => {
                            skuDetail.isActiveForSession = skuDetail.isActive !== undefined ? skuDetail.isActive : false;
                        });
                    }
                });
            }
        },

        // Add a new collection to a cluster
        addCollection: (state, action: PayloadAction<{
            clusterId: string;
            collection: Omit<Collection, 'id'>;
        }>) => {
            const { clusterId, collection } = action.payload;
            const cluster = state.clusters.find(c => c.id === clusterId);
            if (cluster) {
                if (!cluster.collections) {
                    cluster.collections = [];
                }
                // Generate a unique collection ID using epoch time
                const collectionId = `collection-${Date.now()}`;
                cluster.collections.push({
                    ...collection,
                    id: collectionId
                });
            }
        },

        // Update a collection in a cluster
        updateCollection: (state, action: PayloadAction<{
            clusterId: string;
            collectionId: string;
            updates: Partial<Collection>;
        }>) => {
            const { clusterId, collectionId, updates } = action.payload;
            const cluster = state.clusters.find(c => c.id === clusterId);
            if (cluster) {
                const collection = cluster.collections?.find(c => c.id === collectionId);
                if (collection) {
                    Object.assign(collection, updates);
                }
            }
        },

        // Delete a collection from a cluster
        deleteCollection: (state, action: PayloadAction<{
            clusterId: string;
            collectionId: string;
        }>) => {
            const { clusterId, collectionId } = action.payload;
            const cluster = state.clusters.find(c => c.id === clusterId);
            if (cluster && cluster.collections) {
                cluster.collections = cluster.collections.filter(c => c.id !== collectionId);
            }
        },

        // Update cluster form data
        updateClusterFormData: (state, action: PayloadAction<{
            clusterId: string;
            updates: Partial<Omit<Cluster, 'id' | 'name' | 'createdAt' | 'products'>>;
        }>) => {
            const { clusterId, updates } = action.payload;
            const cluster = state.clusters.find(c => c.id === clusterId);
            if (cluster) {
                Object.assign(cluster, updates);
            }
        },

        // Toggle product disabled state in a cluster
        toggleProductDisabled: (state, action: PayloadAction<{
            clusterId: string;
            productId: string;
        }>) => {
            const { clusterId, productId } = action.payload;
            const clusters = state.clusters || [];
            const cluster = clusters.find(c => c.id === clusterId);
            
            if (cluster) {
                const product = cluster.products.find(p => p.id === productId);
                if (product) {
                    product.disabled = !product.disabled;
                }
            }
        },

        // Enable all products in a cluster (set disabled to false)
        enableAllProductsInCluster: (state, action: PayloadAction<string>) => {
            const clusterId = action.payload;
            const clusters = state.clusters || [];
            const cluster = clusters.find(c => c.id === clusterId);

            if (cluster) {
                cluster.products.forEach(product => {
                    product.disabled = false;
                });
            }
        },

        // Toggle selection state of a variant (SKU) inside a product
        toggleVariantSelected: (state, action: PayloadAction<{
            clusterId: string;
            productId: string;
            variantLabel: string; // could be unit or sku label displayed
        }>) => {
            const { clusterId, productId, variantLabel } = action.payload;

            const cluster = state.clusters.find(c => c.id === clusterId);
            if (!cluster) {
                return;
            }

            const product = cluster.products.find(p => p.id === productId);
            if (!product) {
                return;
            }

            const skuDetails: any[] = Array.isArray(product.skuDetails) ? product.skuDetails : [];

            const target = skuDetails.find((s: any) => {
                const unitLabel = (s.unit || "").toString();
                const skuLabel = (s.sku || "").toString();
                const match = unitLabel === variantLabel || skuLabel === variantLabel;
                return match;
            });

            if (target) {
                // Toggle isActiveForSession (session value), not is_active (DB value)
                const currentValue = target.isActiveForSession !== undefined ? target.isActiveForSession : (target.isActive || false);
                target.isActiveForSession = !currentValue;
            } else {
                console.error("Target variant not found");
            }
        },
    },
});

export const {
    setClusters,
    addCluster,
    setCurrentCluster,
    updateProductPriority,
    toggleProductPriority,
    restoreClusterPrioritiesToOriginal,
    toggleVariantSelected,
    restoreClusterVariantsToOriginal,
    addCollection,
    updateCollection,
    deleteCollection,
    updateClusterFormData,
    toggleProductDisabled,
    enableAllProductsInCluster,
    removeCluster,
} = clustersSlice.actions;

export default clustersSlice.reducer;

// Selectors
export const selectClusters = (state: { clusters: ClustersState }) => state.clusters?.clusters || [];
export const selectCurrentCluster = (state: { clusters: ClustersState }) => {
    const clusters = state.clusters?.clusters || [];
    return clusters.find(c => c.id === state.clusters?.currentClusterId);
};
export const selectClusterById = (state: { clusters: ClustersState }, clusterId: string) => {
    const clusters = state.clusters?.clusters || [];
    return clusters.find(c => c.id === clusterId);
};

